// src/components/PurchaseFlow/Steps/DataEntry/DataEntryStep.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import api from '../../../../services/api';
import NavigationButtons from '../../NavigationButtons';
import PersonalInfo from './Sections/PersonalInfo';
import BankInformation from './Sections/BankInformation';
import AddressContact from './Sections/AddressContact';
import ContactInfo from './Sections/ContactInfo';
import Referrals from './Sections/Referrals';
import EmploymentInfo from './Sections/EmploymentInfo';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../context/SuggestedDataContext';
import { trackSaleStepCompleted } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import { isCashPolicyProduct } from '../../../../config/policyProducts';
import './DataEntryContinuous.css';

const KNOWN_GENDER_CODES = new Set(['M', 'F', 'X']);
const SUGGESTED_DATA_TIMEOUT_MS = 20000;

function normalizeGenderFromClient(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (s === '') return '';
  const upper = s.toUpperCase();
  return KNOWN_GENDER_CODES.has(upper) ? upper : '';
}

const DataEntryStep = ({ onNext, onBack, formData: initialData = {}, clientData, purchaseRequestId }) => {
  const { suggestedData, setSuggestedData, selectedData, setSelectedData } = useSuggestedData();
  const { commerceId, branchId } = useAnalyticsContext();

  // Readonly contact fields from env (comma-separated field names)
  const readonlyContactFields = process.env.REACT_APP_READONLY_CONTACT_INFO
    ?.split(',')
    .map(f => f.trim())
    .filter(f => f !== '') || [];

  // Configuración del modo de visualización
  const displayMode = process.env.REACT_APP_DATA_ENTRY_DISPLAY_MODE || 'accordion'; // 'accordion' | 'continuous'
  const isContinuousMode = displayMode === 'continuous';
  
  // Estado local para el banco seleccionado
  const [selectedBank, setSelectedBank] = useState(clientData?.bank || null);
  const seededCashBankIdRef = useRef(clientData?.bank?.id ?? null);

  // Obtener configuración de secciones habilitadas
  const getEnabledSections = () => {
    const configString = process.env.REACT_APP_DATA_ENTRY_STEPS || 'PersonalInfo,AddressContact,ContactInfo,Referrals';
    return configString.split(',').map(section => section.trim());
  };

  const enabledSections = getEnabledSections();
  const isCash = isCashPolicyProduct(initialData.policyProductId);

  useEffect(() => {
    if (!isCash || clientData?.bank?.id == null) {
      return;
    }

    // Seed once per registered bank id. Do not restore after BankInformation
    // clears a catalog-missing bank; that loop would hide the unavailable state.
    if (seededCashBankIdRef.current === clientData.bank.id) {
      return;
    }

    seededCashBankIdRef.current = clientData.bank.id;
    setSelectedBank((current) => current ?? clientData.bank);
  }, [isCash, clientData?.bank]);
  
  // Determinar si se debe mostrar cada sección específica
  const showPersonalInfo = enabledSections.includes('PersonalInfo');
  const showBankSection = isCash || enabledSections.includes('BankInformation');
  const showAddressContact = enabledSections.includes('AddressContact');
  const showContactInfo = enabledSections.includes('ContactInfo');
  const showReferrals = enabledSections.includes('Referrals');
  const showEmploymentSection = enabledSections.includes('EmploymentInfo') && clientData?.employment_status?.code === 'ACTIVE';
  const initialGender = normalizeGenderFromClient(clientData?.gender);
  const initialFormGender = normalizeGenderFromClient(initialData.gender) || initialGender;

  const [formData, setFormData] = useState({
    firstName: clientData?.firstName || '',
    lastName: clientData?.lastName || '',
    birthDate: clientData?.birthDate || '',
    ...(showBankSection && { cbu: '' }),
    referral: [
      { firstName: '', lastName: '', phone: '', relationship: '' },
      { firstName: '', lastName: '', phone: '', relationship: '' }
    ],
    ...initialData,
    gender: initialFormGender,
  });

  const [isLoading, setIsLoading] = useState(false);
  
  // Referencia para prevenir múltiples clics simultáneos
  const isProcessingRef = useRef(false);

  // Inicializar sección activa con la primera sección habilitada
  const getFirstEnabledSection = () => {
    const sectionsInOrder = ['PersonalInfo', 'BankInformation', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
    return sectionsInOrder.find(section => {
      switch (section) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    }) || 'PersonalInfo';
  };

  const [activeSection, setActiveSection] = useState(isContinuousMode ? null : getFirstEnabledSection());
  
  // Configurar validez de secciones solo para las habilitadas
  const getInitialSectionValidity = () => {
    const validity = {};
    if (showPersonalInfo) validity.PersonalInfo = null;
    if (showBankSection) validity.BankInformation = null;
    if (showAddressContact) validity.AddressContact = null;
    if (showContactInfo) validity.ContactInfo = null;
    if (showReferrals) validity.Referrals = null;
    if (showEmploymentSection) validity.EmploymentInfo = null;
    return validity;
  };

  const [sectionValidity, setSectionValidity] = useState(getInitialSectionValidity());

  const [serverErrors, setServerErrors] = useState([]);

  const personalInfoRef = useRef();
  const bankInformationRef = useRef();
  const addressContactRef = useRef();
  const contactInfoRef = useRef();
  const referralsRef = useRef();
  const employmentInfoRef = useRef();

  // Obtener las secciones habilitadas para sugerencias
  const enabledSuggestedSections = useMemo(
    () => (process.env.REACT_APP_SUGGESTED_DATA_ENABLE?.split(',') || []).map((key) => key.trim()).filter(Boolean),
    []
  );

  // Agregar estado para rastrear secciones ya cargadas
  const [loadedSections, setLoadedSections] = useState(new Set());
  const [loadingSections, setLoadingSections] = useState({
    personal: false,
    address: false,
    contact: false,
    referral: false,
    employment: false
  });
  const [suggestionErrors, setSuggestionErrors] = useState({
    personal: false,
    address: false,
    contact: false,
    referral: false,
    employment: false
  });
  const loadedSectionsRef = useRef(loadedSections);
  const suggestedDataInFlightRef = useRef({});
  loadedSectionsRef.current = loadedSections;

  useEffect(() => {
    return () => {
      Object.values(suggestedDataInFlightRef.current).forEach((controller) => controller?.abort?.());
    };
  }, []);

  const loadSuggestedData = useCallback(async (section, { force = false } = {}) => {
    const sectionToEndpoint = {
      PersonalInfo: 'personal',
      AddressContact: 'address',
      ContactInfo: 'contact',
      Referrals: 'referral',
      EmploymentInfo: 'employment'
    };

    const key = sectionToEndpoint[section];
    if (!key || !clientData?.id || !enabledSuggestedSections.includes(key)) {
      return;
    }

    if (!force && (loadedSectionsRef.current.has(key) || suggestedDataInFlightRef.current[key])) {
      return;
    }

    suggestedDataInFlightRef.current[key]?.abort?.();
    const controller = new AbortController();
    suggestedDataInFlightRef.current[key] = controller;

    if (force) {
      setLoadedSections((prev) => {
        const next = new Set(prev);
        next.delete(key);
        loadedSectionsRef.current = next;
        return next;
      });
    }

    setSuggestionErrors((prev) => ({ ...prev, [key]: false }));
    setLoadingSections((prev) => ({ ...prev, [key]: true }));

    const timeoutId = setTimeout(() => controller.abort(), SUGGESTED_DATA_TIMEOUT_MS);

    try {
      const response = await api.get(`/customers/${clientData.id}/suggested-data/${key}`, {
        signal: controller.signal,
        timeout: SUGGESTED_DATA_TIMEOUT_MS
      });
      if (response.data) {
        setSuggestedData((prev) => ({
          ...prev,
          [key]: response.data.data
        }));
        setLoadedSections((prev) => new Set([...prev, key]));
      } else {
        setSuggestionErrors((prev) => ({ ...prev, [key]: true }));
      }
    } catch (error) {
      const superseded = suggestedDataInFlightRef.current[key] !== controller;
      if (superseded) {
        return;
      }
      console.error(`Error loading suggested ${section} data:`, error);
      setSuggestionErrors((prev) => ({ ...prev, [key]: true }));
    } finally {
      clearTimeout(timeoutId);
      if (suggestedDataInFlightRef.current[key] === controller) {
        suggestedDataInFlightRef.current[key] = null;
        setLoadingSections((prev) => ({ ...prev, [key]: false }));
      }
    }
  }, [clientData?.id, enabledSuggestedSections, setSuggestedData]);

  // Efecto para cargar todas las sugerencias en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      const sectionsToLoad = ['PersonalInfo', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
      sectionsToLoad.forEach((section) => {
        const shouldLoad = () => {
          switch (section) {
            case 'PersonalInfo': return showPersonalInfo;
            case 'AddressContact': return showAddressContact;
            case 'ContactInfo': return showContactInfo;
            case 'Referrals': return showReferrals;
            case 'EmploymentInfo': return showEmploymentSection;
            default: return false;
          }
        };

        if (shouldLoad()) {
          loadSuggestedData(section);
        }
      });
    }
  }, [isContinuousMode, showPersonalInfo, showAddressContact, showContactInfo, showReferrals, showEmploymentSection, loadSuggestedData]);

  // Efecto para validación automática en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      // Validar todas las secciones habilitadas automáticamente
      const validateAllSections = () => {
        const sectionsToValidate = [
          { name: 'PersonalInfo', ref: personalInfoRef, enabled: showPersonalInfo },
          { name: 'BankInformation', ref: bankInformationRef, enabled: showBankSection },
          { name: 'AddressContact', ref: addressContactRef, enabled: showAddressContact },
          { name: 'ContactInfo', ref: contactInfoRef, enabled: showContactInfo },
          { name: 'Referrals', ref: referralsRef, enabled: showReferrals },
          { name: 'EmploymentInfo', ref: employmentInfoRef, enabled: showEmploymentSection }
        ];

        sectionsToValidate.forEach(({ name, ref, enabled }) => {
          if (enabled && ref.current) {
            setTimeout(() => {
              ref.current?.validateSection?.();
            }, 100);
          }
        });
      };

      // Validar después de que se carguen las sugerencias
      const timer = setTimeout(validateAllSections, 500);
      return () => clearTimeout(timer);
    }
  }, [isContinuousMode, selectedData, formData, showPersonalInfo, showBankSection, showAddressContact, showContactInfo, showReferrals, showEmploymentSection]);

  const handleChange = (e, options = {}) => {
    const { name, value } = e.target;
  
    let processedValue = value;
    
    // Procesamiento especial para ciertos campos
    if (name === 'phone' || name === 'workPhone') {
      processedValue = value.replace(/^0+/, ''); // Eliminar ceros a la izquierda para teléfono
    }
  
    setFormData((prev) => {
      if (options.isNested && options.arrayIndex !== undefined) {
        const updatedArray = [...prev[options.nestedKey]];
        updatedArray[options.arrayIndex][name] = processedValue;
        return {
          ...prev,
          [options.nestedKey]: updatedArray,
        };
      }
  
      return {
        ...prev,
        [name]: processedValue,
      };
    });
  };  

  const handleSectionClick = (targetSection) => {
    // En modo continuo, no manejar clics de sección
    if (isContinuousMode) {
      return;
    }

    // Verificar que la sección esté habilitada antes de permitir el clic
    const isSectionEnabled = () => {
      switch (targetSection) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    };

    if (!isSectionEnabled()) {
      console.warn(`Section ${targetSection} is not enabled`);
      return;
    }

    let currentSectionRef;

    switch (activeSection) {
      case 'PersonalInfo':
        currentSectionRef = showPersonalInfo ? personalInfoRef : null;
        break;
      case 'BankInformation':
        currentSectionRef = showBankSection ? bankInformationRef : null;
        break;
      case 'AddressContact':
        currentSectionRef = showAddressContact ? addressContactRef : null;
        break;
      case 'ContactInfo':
        currentSectionRef = showContactInfo ? contactInfoRef : null;
        break;
      case 'Referrals':
        currentSectionRef = showReferrals ? referralsRef : null;
        break;
      case 'EmploymentInfo':
        currentSectionRef = showEmploymentSection ? employmentInfoRef : null;
        break;
      default:
        currentSectionRef = null;
    }

    if (currentSectionRef && currentSectionRef.current) {
      currentSectionRef.current.validateSection();
    }

    if (activeSection === targetSection) {
      setActiveSection(null);
    } else {
      setActiveSection(targetSection);
      // Cargar sugerencias cuando se abre una nueva sección
      loadSuggestedData(targetSection);
    } 
  };

  const handleNext = async () => {
    // Prevenir múltiples clics simultáneos
    if (isProcessingRef.current) {
      console.warn('Proceso ya en curso, espere por favor...');
      return;
    }

    let currentSectionRef;
  
    switch (activeSection) {
      case 'PersonalInfo':
        currentSectionRef = showPersonalInfo ? personalInfoRef : null;
        break;
      case 'BankInformation':
        currentSectionRef = showBankSection ? bankInformationRef : null;
        break;
      case 'AddressContact':
        currentSectionRef = showAddressContact ? addressContactRef : null;
        break;
      case 'ContactInfo':
        currentSectionRef = showContactInfo ? contactInfoRef : null;
        break;
      case 'Referrals':
        currentSectionRef = showReferrals ? referralsRef : null;
        break;
      case 'EmploymentInfo':
        currentSectionRef = showEmploymentSection ? employmentInfoRef : null;
        break;
      default:
        currentSectionRef = null;
    }
  
    if (currentSectionRef && currentSectionRef.current && !currentSectionRef.current.validateSection()) {
      console.warn('No puede avanzar sin completar todos los campos requeridos.');
      return;
    }
  
    // Verificar que todas las secciones habilitadas sean válidas
    const enabledSectionsKeys = Object.keys(sectionValidity);
    const allEnabledSectionsValid = enabledSectionsKeys.every((sectionKey) => sectionValidity[sectionKey] === true);
    
    if (allEnabledSectionsValid) {
      // Marcar que el proceso está en curso
      isProcessingRef.current = true;
      setIsLoading(true);
      
      try {
        const success = await handleSubmit();
        if (success) {
          // Track step completed
          if (commerceId) {
            trackSaleStepCompleted({
              commerceId,
              branchId,
              stepName: 'DataEntryStep',
              customerDni: clientData?.dni
            });
          }
          
          onNext({ 
            personalInfo: formData,
            contactPhone: formData.phone // Pasar el teléfono para uso en pasos posteriores
          });
        } else {
          console.error('No se pudo guardar la información. Por favor, revise los errores.');
        }
      } catch (error) {
        console.error('Error en handleNext:', error);
      } finally {
        // Limpiar el estado de procesamiento
        isProcessingRef.current = false;
        setIsLoading(false);
      }
    } else {
      console.warn('Por favor, complete todas las secciones antes de continuar.');
    }
  };  

  const handleBankChange = (newBankData) => {
    setSelectedBank(newBankData);
  };

  const handleSubmit = async () => {
    setServerErrors([]);
    
    const payload = {
      purchase_request_id: purchaseRequestId,
      enabled_sections: isCash && !enabledSections.includes('BankInformation')
        ? [...enabledSections, 'BankInformation']
        : enabledSections,
      validation_rules: {
        referrals: {
          min_count: parseInt(process.env.REACT_APP_MIN_REFERRALS || '1')
        }
      },
      existingData: {
        address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? null : selectedData.address,
        referral: selectedData.referral?.filter(id => !id.toString().startsWith('new_') && !id.toString().startsWith('edited_')) || [],
        contact: (selectedData.contact?.toString().startsWith('new_') || selectedData.contact?.toString().startsWith('edited_') || selectedData.contact?.toString().startsWith('contact_')) ? null : selectedData.contact,
        employment: (selectedData.employment?.toString().startsWith('new_') || selectedData.employment?.toString().startsWith('edited_')) ? null : selectedData.employment
      },
      personalInfo: {
        firstname: formData.firstName,
        lastname: formData.lastName,
        gender: normalizeGenderFromClient(formData.gender) || null,
        birthdate: formData.birthDate,
      },
      ...(showBankSection && selectedBank && {
        bankInfo: {
          bank_id: selectedBank?.id,
          cbu: formData.cbu
        }
      }),
      address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? {
        address: formData.address,
        provinceId: formData.province,
        localityId: formData.locality,
        postalCode: formData.postalCode ? `${formData.postalCode}` : '',
        parent_id: (() => {
          const selectedAddress = suggestedData.address?.find(addr => addr.id === selectedData.address);
          return selectedAddress?.parent_id || null;
        })(),
      } : null,
      contact: (selectedData.contact?.toString().startsWith('new_') || selectedData.contact?.toString().startsWith('edited_') || selectedData.contact?.toString().startsWith('contact_')) ? {
        // Solo incluir campos que NO son readonly o que están vacíos en clientData
        ...((!readonlyContactFields.includes('phone') || !clientData?.phone) && { mobile: formData.phone }),
        ...((!readonlyContactFields.includes('email') || !clientData?.email) && { email: formData.email }),
      } : null,
      referrals: suggestedData.referral
        ?.filter(referral => 
          selectedData.referral?.includes(referral.id) && 
          (referral.id.toString().startsWith('new_') || referral.id.toString().startsWith('edited_'))
        )
        .map(referral => ({
          firstname: referral.firstName,
          lastname: referral.lastName,
          phone: referral.phone,
          relationship: referral.relationship,
        })) || [],
      // Si hay una sugerencia de empleo seleccionada, enviamos null
      ...(showEmploymentSection && {
        employmentInfo: (selectedData.employment?.toString().startsWith('new_') || selectedData.employment?.toString().startsWith('edited_')) ? {
          companyName: formData.companyName,
          entryDate: formData.entryDate,
          position: formData.position,
          workPhone: formData.workPhone,
          workAddress: formData.workAddress,
          workProvinceId: formData.workProvince,
          workLocalityId: formData.workLocality,
          workPostalCode: `${formData.workPostalCode}`,
        } : null
      }),
    };

    console.log('selectedData: ', selectedData); 
    console.log('formData: ', formData); 
    console.log('payload: ', payload);

    try {
      const response = await api.post(`/customers/${clientData.id}/data`, payload);
      if (response.status === 200) {
        console.log('Customer data saved successfully');
        return true; 
      }
    } catch (error) {
      console.error('Error updating customer data:', error);
      
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .map(msg => msg.replace(/employment info\./g, ''));
        
        setServerErrors(errorMessages);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hubo un problema al guardar los datos. Por favor, intente nuevamente.',
        });
      }
      return false;
    }    
  };  

  return (
    <div className="container rg-sale-data-step">
      <h4 className="text-center rg-sale-step__title">Complete sus datos personales</h4>

      {/* Contenedor de formularios - modo accordion o continuo */}
      <div className={isContinuousMode ? "continuous-form-container" : "accordion"} id={isContinuousMode ? "datosContinuous" : "datosAccordion"}>
        {showPersonalInfo && (
          <PersonalInfo
            ref={personalInfoRef}
            formData={formData}
            initialGender={initialGender}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "PersonalInfo" : activeSection}
            sectionName="PersonalInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.personal}
            displayMode={displayMode}
          />
        )}
        {showBankSection && (
          <BankInformation
            ref={bankInformationRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "BankInformation" : activeSection}
            sectionName="BankInformation"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            bankData={selectedBank}
            onBankChange={handleBankChange}
            clientData={clientData}
            displayMode={displayMode}
            cbuLabel={isCash ? 'CBU de acreditación' : 'CBU'}
            requireBankSelection={isCash}
          />
        )}
        {showAddressContact && (
          <AddressContact
            ref={addressContactRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "AddressContact" : activeSection}
            sectionName="AddressContact"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.address}
            suggestionLoadFailed={suggestionErrors.address}
            onRetrySuggestions={() => loadSuggestedData('AddressContact', { force: true })}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showContactInfo && (
          <ContactInfo
            ref={contactInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "ContactInfo" : activeSection}
            sectionName="ContactInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.contact}
            suggestionLoadFailed={suggestionErrors.contact}
            onRetrySuggestions={() => loadSuggestedData('ContactInfo', { force: true })}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showEmploymentSection && (
          <EmploymentInfo
            ref={employmentInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "EmploymentInfo" : activeSection}
            sectionName="EmploymentInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.employment}
            suggestionLoadFailed={suggestionErrors.employment}
            onRetrySuggestions={() => loadSuggestedData('EmploymentInfo', { force: true })}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showReferrals && (
          <Referrals
            ref={referralsRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "Referrals" : activeSection}
            sectionName="Referrals"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.referral}
            suggestionLoadFailed={suggestionErrors.referral}
            onRetrySuggestions={() => loadSuggestedData('Referrals', { force: true })}
            displayMode={displayMode}
          />
        )}
      </div>

      {serverErrors.length > 0 && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong className="alert-heading"><i class="bi bi-exclamation-triangle me-2"></i>Por favor, corrija los siguientes errores:</strong>
          <ul className="mb-0 ms-3 list-unstyled">
            {serverErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <NavigationButtons
        onBack={onBack}
        onNext={handleNext}
        disableNext={!Object.keys(sectionValidity).every((sectionKey) => sectionValidity[sectionKey] === true)}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DataEntryStep;
